import CustomerUpdateService from 'external-services/customer-update-service/customerUpdate.service';
import CustomerOfflineService from 'external-services/customer-offline-service/index';
import { IBooking, ICustomer } from 'shared-types/SharedTypes';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { cloneDeep } from 'lodash-es';
import BookingLookupService from 'external-services/booking-lookup-service/index';
import BookingUpdateService from 'external-services/booking-update-service/index';
import moment from 'moment';

const NS = 'CustomerOfflineSyncService';

const MAX_RETRIES = 5;

const WINDOW_LISTENER_OFFLINE_CUSTOMER_DELETE = 'offline-customer-delete';
const WINDOW_CUT_OFF_DATE_OFFLINE_CUSTOMER_DELETE = 'cut-off-date-offline-customer-delete';

interface ITempCustomer {
  tempCustomer: ICustomer;
  offlineCustomerId: string;
  retries?: number;
}
// @todo: get all functions returning promises properly, so we unit tests don't need timeouts

class CustomerOfflineSyncService {
  private syncQueue: ITempCustomer[];

  private retryTimout = 5000;

  private mockCutOffDate: Date;

  private hasPendingOfflineChanges: boolean;

  constructor() {
    this.addTestListenerForOfflineCustomerDeletes();
  }

  __adjustRetryTimoutForUnitTests(retryTimout: number) {
    this.retryTimout = retryTimout;
  }

  public getSyncQueueLength() {
    return this.syncQueue?.length;
  }

  /**
   * When changes are made in offline mode, this should get set to true.
   * When those changes are then synced as "uptodate" couchStatus, set this to false.
   */
  public setHasPendingOfflineChanges(value: boolean): void {
    this.hasPendingOfflineChanges = value;
  }

  /**
   * Bookings created/update offline for these customers are synced now with the local customers ids
   * Need to update the bookings with the customers new ids
   */
  startSyncingOfflineCustomers() {
    const venue = AdminService.getVenue();
    if (venue && venue.isOnlineCustomersEnabled) {
      const isSyncing = !!this.syncQueue?.length;
      if (!isSyncing) {
        this.syncQueue = [];
      }

      CustomerOfflineService.getAllOffline()
        .then((offlineCustomers) => {
          const tempCustomers = offlineCustomers.filter((c) => c.temporary);
          console.log('Found', tempCustomers.length, 'to migrate online');

          for (const tempCustomer of tempCustomers) {
            if (!this.syncQueue.find((o) => o.offlineCustomerId === tempCustomer._id)) {
              this.syncQueue.push({ tempCustomer, offlineCustomerId: tempCustomer._id });
            }
          }
          // if not already syncing, start syncing
          if (!isSyncing) {
            this.updateBookingWithNewCustomerId(venue.id);
          }
        })
        .catch((err) => console.error(NS, 'Load offline customers', err)); // load offline customers
    }
  }

  /**
   * This is a recursive function that continues until it reaches zero counter.
   * Go through offline customers 1 by one to avoid couch conflicts.
   */
  private updateBookingWithNewCustomerId(venueId: number): void {
    if (!this.syncQueue || !this.syncQueue.length) {
      return;
    }

    const { tempCustomer, offlineCustomerId } = this.syncQueue[0];

    const nextCount = () => {
      this.syncQueue.splice(0, 1);
      setTimeout(() => {
        /**
         * added timeout so large volumes of backed up offline bookings don't freeze the app by trying to do too much
         * too quickly
         */
        this.updateBookingWithNewCustomerId(venueId);
      }, 5000);
    };

    // Add customer online
    CustomerUpdateService.addAsOnlineWhenPossible(tempCustomer, false)
      .then(({ customer, isOnline }) => {
        const onlineCustomerId = customer._id;
        CustomerOfflineService.removeOffline(tempCustomer)
          .then(() => {
            console.log('Moved offline customer', offlineCustomerId, 'to online', onlineCustomerId);

            this.updateBookingsWithOnlineCustomerIds(
              offlineCustomerId,
              onlineCustomerId,
              nextCount
            );
          })
          .catch((err) => {
            if (err.reason === 'deleted') {
              console.error(NS, 'Customer has already been removed', tempCustomer);
              CustomerOfflineService.deleteCustomerFromCache(tempCustomer._id);
              nextCount();
              // this.updateBookingsWithOnlineCustomerIds(offlineCustomerId, onlineCustomerId, nextCount);
            } else {
              console.error(NS, 'Remove offline customer', err);
              nextCount();
            }
          });
      })
      .catch((err) => {
        if (err === 'offline') {
          const retries = this.syncQueue[0].retries || 0;
          if (retries >= MAX_RETRIES) {
            console.log(
              NS,
              `User is still offline. Retried ${retries} times. Giving up.`,
              cloneDeep(this.syncQueue[0])
            );
          } else {
            console.log(
              NS,
              'User is still offline. Waiting 5 seconds and trying again.',
              cloneDeep(this.syncQueue[0])
            );
            this.syncQueue[0].retries = retries + 1;
            setTimeout(() => {
              this.updateBookingWithNewCustomerId(venueId);
            }, this.retryTimout);
          }
        } else {
          console.error('Register customer online', err);

          this.forceRemoveOldOfflineCustomers(venueId, tempCustomer);

          // CustomerOfflineService.removeOffline(tempCustomer)
          nextCount();
        }
      });
  }

  private addTestListenerForOfflineCustomerDeletes(): void {
    window.addEventListener(WINDOW_LISTENER_OFFLINE_CUSTOMER_DELETE, () => {
      this.mockCutOffDate = (window as any)[WINDOW_CUT_OFF_DATE_OFFLINE_CUSTOMER_DELETE] || null;
    });
  }

  /**
   * Looks up offline customers that may have become stuck, due to error saving.
   */
  private forceRemoveOldOfflineCustomers(venueId: number, tempCustomer: ICustomer): void {
    const lastMod: Date = new Date(tempCustomer.lastModifiedDate);
    const cutOffDate: Date = this.mockCutOffDate || moment().add(-1, 'months').toDate();
    if (lastMod > cutOffDate) {
      return; // only remove offline customers before release date
    }
    this.forceRemoveOfflineCustomer(tempCustomer);
  }

  /**
   * NBI-4521: Only a temporary function to force the removal of offline customers with invalid data.
   * Should not be needed again after Jan 2023 and can be removed.
   */
  private forceRemoveOfflineCustomer(tempCustomer: ICustomer): void {
    CustomerOfflineService.removeOffline(tempCustomer)
      .then(() => {
        console.log('Permanently removed offline customer', tempCustomer);
      })
      .catch((err) => {
        if (err.reason === 'deleted') {
          console.warn(NS, 'Customer has already been removed', tempCustomer);
          CustomerOfflineService.deleteCustomerFromCache(tempCustomer._id);
        } else {
          console.warn('Tried to remove offline customer, but failed', tempCustomer, err);
        }
      });
  }

  /**
   * Replaces the pouch-based offline customer id with the SQL-based online customer id
   * @param offlineCustomerId - old pouch id
   * @param onlineCustomerId - new SQL id
   * @param nextCount - callback
   */
  private updateBookingsWithOnlineCustomerIds(
    offlineCustomerId: string,
    onlineCustomerId: string,
    nextCount: () => void
  ): void {
    /**
     * When 'hasPendingOfflineChanges' is true, it means we must access the local pouch db instance to get our offline changes,
     * as they haven't yet been synced with the remote couch db.
     * If 'hasPendingOfflineChanges' is false though, it means we need to access the online couch db instance to get the latest.
     */

    BookingLookupService.getBookingsByCustomerIds(
      [offlineCustomerId],
      !this.hasPendingOfflineChanges
    ).then((bookings: IBooking[]) => {
      if (bookings && bookings.length) {
        for (const booking of bookings) {
          booking.customerId = onlineCustomerId;

          BookingUpdateService.updateInCouchOrPouchWhenOffline(booking, false)
            .then(() => {
              console.log(
                NS,
                'Linked booking',
                booking._id,
                'to online customer',
                onlineCustomerId
              );
              nextCount();
            })
            .catch((err: any) => {
              console.error(
                NS,
                `Failed to link booking with customer id ${onlineCustomerId}`,
                `Still attached to offline id ${offlineCustomerId}`
              );
              nextCount();
            });
        }
      } else {
        console.log(NS, `No bookings found for offline customer id '${offlineCustomerId}'.`);
        nextCount();
      }
    });
  }
}

const instance = new CustomerOfflineSyncService();
export default instance as CustomerOfflineSyncService;
